// alerts-v1 — Alertas del Sistema (servicios / Linkotel / registros / recursos)
import { useState, useEffect, useCallback } from "react";
import api from "../api";

// ── Diccionario ES/EN ───────────────────────────────────────────────────────
// Los identificadores del sistema de alarmas están en INGLÉS porque son claves de la
// base (`alert_key`, `severity`, `event`, `channel`, `delivery_status`). Cómo se
// muestran en castellano lo decide UNA sola fuente: `app/alarm_templates.py`, servida
// por `GET /v1/alerts/dictionary`. Lo de acá abajo es solo el FALLBACK para cuando esa
// llamada no responde; si divergen, manda la del servidor.
//
// Antes esto estaba duplicado (los 7 eventos se nombraban acá Y en la columna `label`
// de las plantillas) y `severity`/`event`/`channel`/`delivery_status` no se traducían
// en ninguna parte: salían crudos en los badges del historial.
let DICT = {
  severidades: { critical: "crítica", high: "alta", warning: "advertencia", info: "informativa" },
  estados:     { firing: "ACTIVA", resolved: "RESUELTA" },
  canales:     { both: "Email + Telegram", email: "Solo Email", telegram: "Solo Telegram", none: "Solo loggear (sin enviar)" },
  entregas:    { ok: "enviada", failed: "falló", skipped: "omitida" },
  etiquetas: {
    service_down:     { label: "Servicio caído",            icon: "🧩", desc: "Algún contenedor del stack dejó de correr." },
    carrier_down:     { label: "Carrier Linkotel caído",    icon: "📞", desc: "El troncal con Linkotel no está disponible (Avail)." },
    no_registrations: { label: "Sin clientes registrados",  icon: "📭", desc: "0 dispositivos registrados en el softswitch." },
    disk_high:        { label: "Disco alto",                icon: "💽", desc: "Uso de disco del host sobre el umbral." },
    mem_high:         { label: "RAM alta",                  icon: "🧠", desc: "Memoria disponible del host bajo el umbral." },
    cpu_high:         { label: "CPU alta",                  icon: "🔥", desc: "Uso de CPU del host sobre el umbral." },
    db_down:          { label: "Base de datos caída/lenta", icon: "🗄️", desc: "MySQL no responde o está lenta (>800ms)." },
  },
};
// tr() nunca oculta un valor: si aparece uno que el diccionario no cubre, lo muestra
// crudo en vez de dejar la celda vacía.
const tr = (grupo, k) => DICT[grupo]?.[k] || k || "—";
const alertLabel = (k) => DICT.etiquetas[k]?.label || k;
const alertIcon  = (k) => DICT.etiquetas[k]?.icon  || "🔔";
const alertDesc  = (k) => DICT.etiquetas[k]?.desc  || "";
const canales    = () => Object.entries(DICT.canales).map(([v, l]) => ({ v, l }));

// "hace 3 h 20 m" — una alarma activa se lee por cuánto lleva, no por su timestamp.
function desde(segundos) {
  if (segundos == null) return "—";
  const d = Math.floor(segundos / 86400), h = Math.floor(segundos % 86400 / 3600), m = Math.floor(segundos % 3600 / 60);
  if (d) return `${d} d ${h} h`;
  if (h) return `${h} h ${m} m`;
  return `${m} m`;
}

function badgeForChannel(c) {
  if (c === "none") return "nv-badge-muted";
  if (c === "telegram") return "nv-badge-warn";
  return "nv-badge-ok";
}
function badgeForDelivery(d) {
  if (d === "ok") return "nv-badge-ok";
  if (d === "failed") return "nv-badge-err";
  return "nv-badge-muted"; // skipped
}
function badgeForEvent(e) {
  return e === "resolved" ? "nv-badge-ok" : "nv-badge-err";
}
function badgeForSeverity(s) {
  if (s === "critical") return "nv-badge-err";
  if (s === "high" || s === "warning") return "nv-badge-warn";
  return "nv-badge-muted";
}

function Alert({ msg, onClose }) {
  if (!msg) return null;
  const cls = { success: "nv-alert-ok", error: "nv-alert-err", warn: "nv-alert-warn" }[msg.type] || "nv-alert-ok";
  return (
    <div className={"nv-alert " + cls} style={{ marginBottom: 14 }}>
      <span style={{ flex: 1 }}>{msg.text}</span>
      <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", fontSize: 14 }}>✕</button>
    </div>
  );
}

function RuleModal({ rule, onClose, onSaved }) {
  const [form, setForm] = useState({
    enabled: rule.enabled !== 0 && rule.enabled !== false,
    debounce_seconds: rule.debounce_seconds ?? 60,
    cooldown_seconds: rule.cooldown_seconds ?? 600,
    channel: rule.channel || "both",
    recipients_override: rule.recipients_override || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await api.put(`/v1/alerts/rules/${rule.alert_key}`, {
        enabled: !!form.enabled,
        debounce_seconds: parseInt(form.debounce_seconds, 10) || 0,
        cooldown_seconds: parseInt(form.cooldown_seconds, 10) || 0,
        channel: form.channel,
        recipients_override: form.recipients_override || null,
      });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error guardando regla");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="nv-modal-overlay" onClick={onClose}>
      <div className="nv-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="nv-modal-header">
          <h3>{alertIcon(rule.alert_key)} Editar — {alertLabel(rule.alert_key)}</h3>
          <button className="nv-icon-btn" onClick={onClose}>✕</button>
        </div>

        {err && <div className="nv-alert nv-alert-err" style={{ margin: "10px 0" }}>{err}</div>}
        <p style={{ color: "var(--text-dim)", fontSize: 13, margin: "4px 0 12px" }}>
          {alertDesc(rule.alert_key)}
        </p>

        <div className="nv-form-grid">
          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="checkbox" checked={!!form.enabled} onChange={e => set("enabled", e.target.checked)} />
            Habilitada (el watcher la vigila)
          </label>

          <label>Debounce (segundos)
            <input type="number" min="0" value={form.debounce_seconds}
                   onChange={e => set("debounce_seconds", e.target.value)} />
            <small style={{ color: "var(--text-dim)" }}>La condición debe sostenerse este tiempo antes de alertar (evita falsos por parpadeo).</small>
          </label>

          <label>Cooldown (segundos)
            <input type="number" min="0" value={form.cooldown_seconds}
                   onChange={e => set("cooldown_seconds", e.target.value)} />
            <small style={{ color: "var(--text-dim)" }}>No re-envía la misma alerta antes de este tiempo.</small>
          </label>

          <label>Canal
            <select value={form.channel} onChange={e => set("channel", e.target.value)}>
              {canales().map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
            </select>
          </label>

          <label>Destinatarios override (opcional)
            <input type="text" value={form.recipients_override || ""}
                   onChange={e => set("recipients_override", e.target.value)}
                   placeholder="email1@x.com,email2@y.com (vacío = usa ALERT_EMAIL_TO de .env)" />
          </label>
        </div>

        <div className="nv-modal-actions">
          <button className="nv-btn-ghost" onClick={onClose} disabled={busy}>Cancelar</button>
          <button className="nv-btn-primary" onClick={submit} disabled={busy}>
            {busy ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}


export default function Alerts() {
  const [rules, setRules] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [custom, setCustom] = useState([]);
  const [metrics, setMetrics] = useState({ metrics: [], operators: [], severities: [], channels: [] });
  const [caForm, setCaForm] = useState({ name:"", metric:"disk_used_pct", operator:">", threshold:80, severity:"warning", channel:"both" });
  const [caBusy, setCaBusy] = useState(false);
  const [caDel, setCaDel] = useState(null);
  const [kpiSnap, setKpiSnap] = useState({ window_min: 15, snapshot: { global:{}, carrier:{}, destino:{} } });
  const [kpiRules, setKpiRules] = useState([]);
  const [kpiCat, setKpiCat] = useState({ metrics: [], dims: [], comparisons: [], severities: [], channels: [] });
  const [kpiForm, setKpiForm] = useState({ name:"", metric:"asr", dim:"global", dim_key:"*", comparison:"abs_floor", threshold:35, min_attempts:30, severity:"warning", channel:"both" });
  const [kpiBusy, setKpiBusy] = useState(false);
  const [kpiDel, setKpiDel] = useState(null);
  const [sipRules, setSipRules] = useState([]);
  const [sipCat, setSipCat] = useState({ indicators: [], operators: [], severities: [], channels: [] });
  const [sipForm, setSipForm] = useState({ name:"", mode:"catalog", indicator:"registros_activos", promql:"", window_s:"5m", operator:"<", threshold:1, severity:"warning", channel:"both" });
  const [sipBusy, setSipBusy] = useState(false);
  const [sipDel, setSipDel] = useState(null);
  const [, setDictReady] = useState(0);   // fuerza re-render cuando llega el diccionario
  const [watch, setWatch] = useState([]);
  const [watchCand, setWatchCand] = useState({ clientes: [], lineas: [] });
  const [wForm, setWForm] = useState({ tipo:"cliente", entidad_id:"", severity:"high", channel:"both", debounce_seconds:300 });
  const [wBusy, setWBusy] = useState(false);
  const [activas, setActivas] = useState([]);
  const [watcher, setWatcher] = useState(null);
  // OJO: NO llamar a este estado `canales` — hay un helper `canales()` a nivel de módulo
  // (línea ~36) y el estado lo tapaba dentro del componente, dejando los `canales()`
  // de los <select> llamando a un objeto. TypeError en el render y pantalla en blanco.
  const [saludCanales, setSaludCanales] = useState(null);
  const [agrupado, setAgrupado] = useState([]);
  const [verAgrupado, setVerAgrupado] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rRules, rHist, cm, mt] = await Promise.all([
        api.get("/v1/alerts/rules"),
        api.get("/v1/alerts/history?limit=50"),
        api.get("/v1/alerts/custom").catch(()=>null),
        api.get("/v1/alerts/metrics").catch(()=>null),
      ]);
      setRules(rRules.data?.data || []);
      setHistory(rHist.data?.data || []);
      setCustom(cm?.data?.data || []);
      if (mt?.data) setMetrics(mt.data);
      const [ks, kr, kc] = await Promise.all([
        api.get("/v1/alerts/kpi/snapshot").catch(()=>null),
        api.get("/v1/alerts/kpi/rules").catch(()=>null),
        api.get("/v1/alerts/kpi/catalog").catch(()=>null),
      ]);
      if (ks?.data) setKpiSnap(ks.data);
      setKpiRules(kr?.data?.data || []);
      if (kc?.data) setKpiCat(kc.data);
      const [sr, sc] = await Promise.all([
        api.get("/v1/alerts/sip/rules").catch(()=>null),
        api.get("/v1/alerts/sip/catalog").catch(()=>null),
      ]);
      setSipRules(sr?.data?.data || []);
      if (sc?.data) setSipCat(sc.data);
      const dc = await api.get("/v1/alerts/dictionary").catch(()=>null);
      // El diccionario del server pisa al fallback local. Si la llamada falla, se
      // sigue con el fallback: la pantalla nunca queda sin nombres.
      if (dc?.data?.etiquetas) { DICT = dc.data; setDictReady(d => d + 1); }
      const [wl, wc] = await Promise.all([
        api.get("/v1/alerts/watch").catch(()=>null),
        api.get("/v1/alerts/watch/candidates").catch(()=>null),
      ]);
      setWatch(wl?.data?.data || []);
      if (wc?.data) setWatchCand(wc.data);
      const [ac, ch, hg] = await Promise.all([
        api.get("/v1/alerts/active").catch(()=>null),
        api.get("/v1/alerts/channels").catch(()=>null),
        api.get("/v1/alerts/history/grouped?horas=168").catch(()=>null),
      ]);
      setActivas(ac?.data?.data || []);
      setWatcher(ac?.data?.watcher || null);
      setSaludCanales(ch?.data?.data || null);
      setAgrupado(hg?.data?.data || []);
    } catch (e) {
      setMsg({ type: "error", text: "No se pudieron cargar las alertas: " + (e?.response?.data?.detail || e.message) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const createWatch = async () => {
    if (!wForm.entidad_id) return;
    setWBusy(true);
    try {
      await api.post("/v1/alerts/watch", { ...wForm, debounce_seconds: parseInt(wForm.debounce_seconds, 10) || 300 });
      setMsg({ type:"success", text:"Entidad vigilada. El watcher la toma en el próximo ciclo, sin reiniciar." });
      setWForm(f => ({ ...f, entidad_id:"" }));
      loadAll();
    } catch (e) {
      setMsg({ type:"error", text: e?.response?.data?.detail || "No se pudo crear la vigilancia" });
    } finally { setWBusy(false); }
  };

  const deleteWatch = async (w) => {
    try {
      await api.delete(`/v1/alerts/watch/${w.id}`);
      setMsg({ type:"success", text:`Ya no se vigila ${w.nombre}.` });
      loadAll();
    } catch (e) {
      setMsg({ type:"error", text: e?.response?.data?.detail || "No se pudo eliminar" });
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/v1/alerts/test", { to: testTo || null, channel: "email" });
      const [st, info] = r.data?.email || [];
      if (st === "ok") {
        setMsg({ type: "success", text: "Email de prueba enviado a: " + info });
      } else if (st === "skipped") {
        setMsg({ type: "warn", text: "No se envió (SMTP no configurado): " + info });
      } else {
        setMsg({ type: "error", text: "Falló el envío: " + (info || "error desconocido") });
      }
      loadAll();
    } catch (e) {
      setMsg({ type: "error", text: "Error: " + (e?.response?.data?.detail || e.message) });
    } finally {
      setTesting(false);
    }
  };

  const createCustom = async () => {
    if (!caForm.name.trim()) return setMsg({ type: "warn", text: "Poné un nombre" });
    setCaBusy(true);
    try {
      await api.post("/v1/alerts/custom", { ...caForm, threshold: parseFloat(caForm.threshold) });
      setMsg({ type: "success", text: "Alerta creada" });
      setCaForm(f=>({ ...f, name:"" }));
      loadAll();
    } catch(e) { setMsg({ type: "error", text: e?.response?.data?.detail || "Error al crear" }); }
    finally { setCaBusy(false); }
  };

  const deleteCustom = async (ca) => {
    try { await api.delete("/v1/alerts/custom/"+ca.id); setMsg({ type: "success", text: "Alerta eliminada" }); loadAll(); }
    catch(e) { setMsg({ type: "error", text: "Error al eliminar" }); }
    setCaDel(null);
  };

  const createKpi = async () => {
    if (!kpiForm.name.trim()) return setMsg({ type:"warn", text:"Poné un nombre" });
    setKpiBusy(true);
    try {
      await api.post("/v1/alerts/kpi/rules", { ...kpiForm, threshold: parseFloat(kpiForm.threshold), min_attempts: parseInt(kpiForm.min_attempts,10) });
      setMsg({ type:"success", text:"Regla KPI creada" });
      setKpiForm(f=>({ ...f, name:"" }));
      loadAll();
    } catch(e) { setMsg({ type:"error", text: e?.response?.data?.detail || "Error al crear" }); }
    finally { setKpiBusy(false); }
  };
  const deleteKpi = async (r) => {
    try { await api.delete("/v1/alerts/kpi/rules/"+r.id); setMsg({ type:"success", text:"Regla eliminada" }); loadAll(); }
    catch(e) { setMsg({ type:"error", text:"Error al eliminar" }); }
    setKpiDel(null);
  };
  const fmtKpi = (v) => (v===null||v===undefined) ? "—" : v;

  const createSip = async () => {
    if (!sipForm.name.trim()) return setMsg({ type:"warn", text:"Poné un nombre" });
    if (sipForm.mode==="promql" && !sipForm.promql.trim()) return setMsg({ type:"warn", text:"Poné el PromQL" });
    setSipBusy(true);
    try {
      await api.post("/v1/alerts/sip/rules", { ...sipForm, threshold: parseFloat(sipForm.threshold) });
      setMsg({ type:"success", text:"Alerta SIP creada" });
      setSipForm(f=>({ ...f, name:"" }));
      loadAll();
    } catch(e) { setMsg({ type:"error", text: e?.response?.data?.detail || "Error al crear" }); }
    finally { setSipBusy(false); }
  };
  const deleteSip = async (r) => {
    try { await api.delete("/v1/alerts/sip/rules/"+r.id); setMsg({ type:"success", text:"Alerta eliminada" }); loadAll(); }
    catch(e) { setMsg({ type:"error", text:"Error al eliminar" }); }
    setSipDel(null);
  };
  const sipExpr = (r) => r.mode==="promql" ? r.promql : (r.indicator+" ["+r.window_s+"]");

  return (
    <div>
      <div className="nv-page-header">
        <div>
          <h1>Alertas del Sistema</h1>
          <p className="nv-page-sub">
            El watcher vigila <strong>servicios</strong>, <strong>Linkotel</strong>, <strong>registros</strong> y <strong>recursos</strong> del host
            y avisa por Email/Telegram. <strong>Debounce</strong> evita falsas alarmas; <strong>cooldown</strong> evita repetir.
          </p>
        </div>
      </div>

      <Alert msg={msg} onClose={() => setMsg(null)} />

      {/* ────── LATIDO DEL WATCHER ──────
          Va primero y en rojo porque es peor que cualquier alarma de la lista: si el
          watcher no corre, no hay quien evalúe nada y un incidente nuevo NO avisa. */}
      {watcher && !watcher.ok && (
        <div className="nv-alert nv-alert-err" style={{ marginBottom: 14 }}>
          <span style={{ flex: 1 }}>
            ⛔ <strong>El watcher no está reportando.</strong>{" "}
            {watcher.ultimo_ciclo
              ? <>Último ciclo hace {desde(watcher.atraso_segundos)} ({watcher.ultimo_ciclo}).</>
              : <>Nunca reportó desde que existe esta pantalla.</>}
            {" "}Mientras esté así, <strong>ninguna alarma se va a disparar</strong> —
            revisar <code>netvoice-alerts-watcher.service</code> en el host.
          </span>
        </div>
      )}

      {/* ────── SALUD DE LOS CANALES ──────
          Entre el 27-jun y el 13-ago se perdieron 45 avisos de Telegram con el token vacío
          y la pantalla no lo decía: el dato estaba en delivery_status y nadie lo leía. */}
      {saludCanales && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
          {Object.entries(saludCanales).map(([nombre, c]) => {
            const color = { ok: "var(--ok)", error: "var(--danger)", sin_configurar: "var(--warn)" }[c.estado] || "var(--text-dim)";
            const icono = { ok: "✅", error: "🔴", sin_configurar: "⚠️" }[c.estado] || "○";
            return (
              <div key={nombre} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px",
                                         background: "var(--bg-raised)", borderRadius: "var(--r-sm)",
                                         borderLeft: `3px solid ${color}` }}>
                <span>{icono}</span>
                <div style={{ fontSize: 12, lineHeight: 1.45 }}>
                  <strong style={{ textTransform: "capitalize" }}>{nombre}</strong>{" · "}
                  {c.estado === "sin_configurar"
                    ? <span style={{ color: "var(--warn)" }}>sin configurar, y {c.reglas_que_lo_usan} regla(s) lo usan</span>
                    : c.estado === "inactivo"
                      ? <span style={{ color: "var(--text-dim)" }}>sin configurar, ninguna regla lo usa</span>
                      : <>{c.ok_30d} entregados en 30 d{c.fallidos_30d ? <span style={{ color: "var(--danger)" }}> · {c.fallidos_30d} fallidos</span> : null}</>}
                  {c.omitidos_30d > 0 && <span style={{ color: "var(--text-dim)" }}> · {c.omitidos_30d} omitidos</span>}
                  <div style={{ color: "var(--text-dim)" }}>
                    {c.ultimo_ok ? `último entregado: ${c.ultimo_ok}` : "nunca entregó"}
                    {!c.destino_por_defecto && " · sin destinatario por defecto"}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ────── ALARMAS ACTIVAS ────── */}
      <div className="nv-card" style={{ marginBottom: 18,
           borderLeft: activas.length ? "3px solid var(--danger)" : "3px solid var(--ok)" }}>
        <div className="nv-card-header">
          <span className="nv-card-title">{activas.length ? "🔴" : "🟢"} Alarmas activas</span>
          <span style={{ fontSize: 12, color: "var(--text-dim)" }}>
            {activas.length ? `${activas.length} activa(s)` : "ninguna"} · {watcher?.vigiladas ?? 0} condiciones vigiladas
          </span>
        </div>
        {activas.length === 0 ? (
          <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
            Nada activo. {watcher?.ok && <>El watcher evaluó hace {desde(watcher.atraso_segundos)}.</>}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {activas.map(a => (
              <div key={a.alert_key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px",
                                              background: "var(--bg-raised)", borderRadius: "var(--r-sm)" }}>
                <span className={"nv-badge " + badgeForSeverity(a.severity)}>{a.severidad}</span>
                <span style={{ flex: 1, fontSize: 13 }}>
                  <strong>{a.etiqueta}</strong>
                  <div style={{ color: "var(--text-dim)", fontSize: 12 }}>{a.summary}</div>
                </span>
                <span style={{ fontSize: 12, color: "var(--text-dim)", whiteSpace: "nowrap" }}
                      title={`activa desde ${a.firing_since || "?"}`}>
                  hace {desde(a.segundos_activa)}
                </span>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)" }}>{a.alert_key}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ────── PRUEBA DE ENVÍO ────── */}
      <div className="nv-card" style={{ marginBottom: 18 }}>
        <h3>Prueba de envío</h3>
        <p style={{ color: "var(--text-dim)", fontSize: 13, marginBottom: 10 }}>
          Envía un email de prueba (canal Email). Si <code>SMTP_USER/PASSWORD</code> están vacíos en <code>.env</code> verás el aviso correspondiente.
        </p>
        <div style={{ display: "flex", gap: 10 }}>
          <input
            type="email"
            placeholder="Destinatario (vacío = usa ALERT_EMAIL_TO)"
            value={testTo}
            onChange={e => setTestTo(e.target.value)}
            style={{ flex: 1 }}
          />
          <button className="nv-btn-primary" onClick={sendTest} disabled={testing}>
            {testing ? "Enviando…" : "Enviar prueba"}
          </button>
        </div>
      </div>

      {/* ────── REGLAS ────── */}
      <div className="nv-card" style={{ marginBottom: 18 }}>
        <h3>Reglas configuradas</h3>
        {loading ? <p>Cargando…</p> : (
          <table className="nv-table">
            <thead>
              <tr>
                <th>Alerta</th>
                <th>Debounce</th>
                <th>Cooldown</th>
                <th>Canal</th>
                <th>Destinatarios</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rules.map(r => (
                <tr key={r.alert_key}>
                  <td><strong>{alertIcon(r.alert_key)} {alertLabel(r.alert_key)}</strong>
                    <div style={{ fontSize: 11, color: "var(--text-dim)", fontFamily: "var(--font-mono)" }}>{r.alert_key}</div>
                  </td>
                  <td>{r.debounce_seconds}s</td>
                  <td>{r.cooldown_seconds}s</td>
                  <td><span className={"nv-badge " + badgeForChannel(r.channel)}>{tr("canales", r.channel)}</span></td>
                  <td style={{ fontSize: 12, color: "var(--text-dim)" }}>
                    {r.recipients_override || "(default .env)"}
                  </td>
                  <td>
                    {(r.enabled === 1 || r.enabled === true)
                      ? <span className="nv-badge nv-badge-ok">ON</span>
                      : <span className="nv-badge nv-badge-muted">OFF</span>}
                  </td>
                  <td>
                    <button className="nv-icon-btn" title="Editar" onClick={() => setEditing(r)}>✎</button>
                  </td>
                </tr>
              ))}
              {rules.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: "center", color: "var(--text-dim)" }}>Sin reglas</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* ────── ALERTAS PERSONALIZADAS ────── */}
      <div className="nv-card" style={{ marginBottom:16 }}>
        <div className="nv-card-header"><span className="nv-card-title">➕ Alertas personalizadas</span></div>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"flex-end", marginBottom:12 }}>
          <div className="nv-form-field" style={{ flex:"1 1 160px" }}>
            <label className="nv-label">Nombre</label>
            <input className="nv-input" value={caForm.name} onChange={e=>setCaForm(f=>({...f,name:e.target.value}))} placeholder="ej: Disco crítico"/>
          </div>
          <div className="nv-form-field">
            <label className="nv-label">Métrica</label>
            <select className="nv-select" value={caForm.metric} onChange={e=>setCaForm(f=>({...f,metric:e.target.value}))}>
              {metrics.metrics.map(m=><option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </div>
          <div className="nv-form-field" style={{ maxWidth:80 }}>
            <label className="nv-label">Op.</label>
            <select className="nv-select" value={caForm.operator} onChange={e=>setCaForm(f=>({...f,operator:e.target.value}))}>
              {metrics.operators.map(o=><option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div className="nv-form-field" style={{ maxWidth:100 }}>
            <label className="nv-label">Valor</label>
            <input className="nv-input" type="number" value={caForm.threshold} onChange={e=>setCaForm(f=>({...f,threshold:e.target.value}))}/>
          </div>
          <div className="nv-form-field">
            <label className="nv-label">Severidad</label>
            <select className="nv-select" value={caForm.severity} onChange={e=>setCaForm(f=>({...f,severity:e.target.value}))}>
              {(metrics.severities||["info","warning","high","critical"]).map(s=><option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="nv-form-field">
            <label className="nv-label">Canal</label>
            <select className="nv-select" value={caForm.channel} onChange={e=>setCaForm(f=>({...f,channel:e.target.value}))}>
              {canales().map(c=><option key={c.v} value={c.v}>{c.l}</option>)}
            </select>
          </div>
          <button className="nv-btn nv-btn-primary" onClick={createCustom} disabled={caBusy}>
            {caBusy?<span className="nv-spinner"/>:"+ Crear"}
          </button>
        </div>
        {custom.length===0 ? (
          <div style={{ fontSize:12, color:"var(--text-muted)", padding:"8px 0" }}>Sin alertas personalizadas.</div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            {custom.map(ca=>(
              <div key={ca.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 10px", background:"var(--bg-raised)", borderRadius:"var(--r-sm)" }}>
                <span style={{ flex:1, fontSize:13 }}>
                  <strong>{ca.name}</strong> · <span style={{ fontFamily:"var(--font-mono)" }}>{ca.metric} {ca.operator} {ca.threshold}</span>
                </span>
                <span className={"nv-badge "+badgeForSeverity(ca.severity)}>{ca.severity}</span>
                <span className={"nv-badge "+badgeForChannel(ca.channel)}>{ca.channel}</span>
                <button className="nv-btn nv-btn-danger nv-btn-sm" onClick={()=>setCaDel(ca)} title="Eliminar">✕</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ────── KPIs DE TELEFONÍA ────── */}
      <div className="nv-card" style={{ marginBottom:16 }}>
        <div className="nv-card-header"><span className="nv-card-title">📞 KPIs de telefonía</span>
          <span style={{ fontSize:12, color:"var(--text-dim)" }}>ventana {kpiSnap.window_min} min</span></div>

        <div style={{ display:"flex", gap:16, flexWrap:"wrap", marginBottom:12 }}>
          {["global","carrier","destino"].map(dim=>(
            <div key={dim} style={{ flex:"1 1 220px" }}>
              <div style={{ fontSize:12, color:"var(--text-muted)", textTransform:"uppercase", marginBottom:4 }}>{dim}</div>
              {Object.keys(kpiSnap.snapshot?.[dim]||{}).length===0 ? (
                <div style={{ fontSize:12, color:"var(--text-dim)" }}>sin tráfico</div>
              ) : Object.entries(kpiSnap.snapshot[dim]).map(([k,m])=>(
                <div key={k} style={{ fontSize:12, fontFamily:"var(--font-mono)", padding:"2px 0" }}>
                  <strong>{k}</strong>: ASR {fmtKpi(m.asr)} · NER {fmtKpi(m.ner)} · ACD {fmtKpi(m.acd)} · CPS% {fmtKpi(m.cps_pct)} <span style={{color:"var(--text-dim)"}}>({m.attempts} int.)</span>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"flex-end", marginBottom:12 }}>
          <div className="nv-form-field" style={{ flex:"1 1 140px" }}>
            <label className="nv-label">Nombre</label>
            <input className="nv-input" value={kpiForm.name} onChange={e=>setKpiForm(f=>({...f,name:e.target.value}))} placeholder="ej: ASR bajo global"/>
          </div>
          <div className="nv-form-field"><label className="nv-label">Métrica</label>
            <select className="nv-select" value={kpiForm.metric} onChange={e=>setKpiForm(f=>({...f,metric:e.target.value}))}>
              {kpiCat.metrics.map(m=><option key={m.key} value={m.key}>{m.label}</option>)}</select></div>
          <div className="nv-form-field"><label className="nv-label">Dimensión</label>
            <select className="nv-select" value={kpiForm.dim} onChange={e=>setKpiForm(f=>({...f,dim:e.target.value}))}>
              {kpiCat.dims.map(d=><option key={d} value={d}>{d}</option>)}</select></div>
          <div className="nv-form-field" style={{ maxWidth:110 }}><label className="nv-label">Clave (* = todas)</label>
            <input className="nv-input" value={kpiForm.dim_key} onChange={e=>setKpiForm(f=>({...f,dim_key:e.target.value}))}/></div>
          <div className="nv-form-field"><label className="nv-label">Comparación</label>
            <select className="nv-select" value={kpiForm.comparison} onChange={e=>setKpiForm(f=>({...f,comparison:e.target.value}))}>
              {kpiCat.comparisons.map(c=><option key={c} value={c}>{c}</option>)}</select></div>
          <div className="nv-form-field" style={{ maxWidth:90 }}><label className="nv-label">Umbral</label>
            <input className="nv-input" type="number" value={kpiForm.threshold} onChange={e=>setKpiForm(f=>({...f,threshold:e.target.value}))}/></div>
          <div className="nv-form-field" style={{ maxWidth:90 }}><label className="nv-label">Mín. int.</label>
            <input className="nv-input" type="number" value={kpiForm.min_attempts} onChange={e=>setKpiForm(f=>({...f,min_attempts:e.target.value}))}/></div>
          <div className="nv-form-field"><label className="nv-label">Severidad</label>
            <select className="nv-select" value={kpiForm.severity} onChange={e=>setKpiForm(f=>({...f,severity:e.target.value}))}>
              {(kpiCat.severities||[]).map(s=><option key={s} value={s}>{s}</option>)}</select></div>
          <div className="nv-form-field"><label className="nv-label">Canal</label>
            <select className="nv-select" value={kpiForm.channel} onChange={e=>setKpiForm(f=>({...f,channel:e.target.value}))}>
              {canales().map(c=><option key={c.v} value={c.v}>{c.l}</option>)}</select></div>
          <button className="nv-btn nv-btn-primary" onClick={createKpi} disabled={kpiBusy}>
            {kpiBusy?<span className="nv-spinner"/>:"+ Crear regla"}</button>
        </div>

        {kpiRules.length===0 ? (
          <div style={{ fontSize:12, color:"var(--text-muted)" }}>Sin reglas KPI.</div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            {kpiRules.map(r=>(
              <div key={r.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 10px", background:"var(--bg-raised)", borderRadius:"var(--r-sm)" }}>
                <span style={{ flex:1, fontSize:13 }}><strong>{r.name}</strong> · <span style={{ fontFamily:"var(--font-mono)" }}>{r.metric} {r.dim}:{r.dim_key} {r.comparison} {r.threshold}</span></span>
                <span className={"nv-badge "+badgeForSeverity(r.severity)}>{tr("severidades", r.severity)}</span>
                <span className={"nv-badge "+badgeForChannel(r.channel)}>{tr("canales", r.channel)}</span>
                {!r.enabled && <span className="nv-badge">off</span>}
                <button className="nv-btn nv-btn-danger nv-btn-sm" onClick={()=>setKpiDel(r)} title="Eliminar">✕</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ────── ALERTAS SIP ────── */}
      <div className="nv-card" style={{ marginBottom:16 }}>
        <div className="nv-card-header"><span className="nv-card-title">📡 Alertas SIP</span>
          <span style={{ fontSize:12, color:"var(--text-dim)" }}>fuente: Prometheus</span></div>

        <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"flex-end", marginBottom:12 }}>
          <div className="nv-form-field" style={{ flex:"1 1 140px" }}>
            <label className="nv-label">Nombre</label>
            <input className="nv-input" value={sipForm.name} onChange={e=>setSipForm(f=>({...f,name:e.target.value}))} placeholder="ej: 408 alto"/>
          </div>
          <div className="nv-form-field"><label className="nv-label">Modo</label>
            <select className="nv-select" value={sipForm.mode} onChange={e=>setSipForm(f=>({...f,mode:e.target.value}))}>
              <option value="catalog">Catálogo</option><option value="promql">PromQL</option></select></div>
          {sipForm.mode==="catalog" ? (
            <div className="nv-form-field"><label className="nv-label">Indicador</label>
              <select className="nv-select" value={sipForm.indicator} onChange={e=>setSipForm(f=>({...f,indicator:e.target.value}))}>
                {sipCat.indicators.map(i=><option key={i.key} value={i.key}>{i.label}</option>)}</select></div>
          ) : (
            <div className="nv-form-field" style={{ flex:"1 1 220px" }}><label className="nv-label">PromQL</label>
              <input className="nv-input" value={sipForm.promql} onChange={e=>setSipForm(f=>({...f,promql:e.target.value}))} placeholder="kamailio_core_rcv_replies_5xx"/></div>
          )}
          <div className="nv-form-field" style={{ maxWidth:80 }}><label className="nv-label">Ventana</label>
            <input className="nv-input" value={sipForm.window_s} onChange={e=>setSipForm(f=>({...f,window_s:e.target.value}))}/></div>
          <div className="nv-form-field" style={{ maxWidth:80 }}><label className="nv-label">Op.</label>
            <select className="nv-select" value={sipForm.operator} onChange={e=>setSipForm(f=>({...f,operator:e.target.value}))}>
              {sipCat.operators.map(o=><option key={o} value={o}>{o}</option>)}</select></div>
          <div className="nv-form-field" style={{ maxWidth:90 }}><label className="nv-label">Umbral</label>
            <input className="nv-input" type="number" value={sipForm.threshold} onChange={e=>setSipForm(f=>({...f,threshold:e.target.value}))}/></div>
          <div className="nv-form-field"><label className="nv-label">Severidad</label>
            <select className="nv-select" value={sipForm.severity} onChange={e=>setSipForm(f=>({...f,severity:e.target.value}))}>
              {(sipCat.severities||[]).map(s=><option key={s} value={s}>{s}</option>)}</select></div>
          <div className="nv-form-field"><label className="nv-label">Canal</label>
            <select className="nv-select" value={sipForm.channel} onChange={e=>setSipForm(f=>({...f,channel:e.target.value}))}>
              {canales().map(c=><option key={c.v} value={c.v}>{c.l}</option>)}</select></div>
          <button className="nv-btn nv-btn-primary" onClick={createSip} disabled={sipBusy}>
            {sipBusy?<span className="nv-spinner"/>:"+ Crear"}</button>
        </div>

        {sipRules.length===0 ? (
          <div style={{ fontSize:12, color:"var(--text-muted)" }}>Sin alertas SIP.</div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            {sipRules.map(r=>(
              <div key={r.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 10px", background:"var(--bg-raised)", borderRadius:"var(--r-sm)" }}>
                <span style={{ flex:1, fontSize:13 }}><strong>{r.name}</strong> · <span style={{ fontFamily:"var(--font-mono)" }}>{sipExpr(r)} {r.operator} {r.threshold}</span></span>
                <span style={{ fontSize:12, color:"var(--text-dim)" }}>{r.last_error ? "⚠ "+r.last_error : (r.last_value!==null && r.last_value!==undefined ? "val "+r.last_value : "—")}</span>
                <span className={"nv-badge "+badgeForSeverity(r.severity)}>{tr("severidades", r.severity)}</span>
                <span className={"nv-badge "+badgeForChannel(r.channel)}>{tr("canales", r.channel)}</span>
                {!r.enabled && <span className="nv-badge">off</span>}
                <button className="nv-btn nv-btn-danger nv-btn-sm" onClick={()=>setSipDel(r)} title="Eliminar">✕</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="nv-card" style={{ marginBottom:16 }}>
        <div className="nv-card-header"><span className="nv-card-title">👁 Entidades vigiladas</span>
          <span style={{ fontSize:12, color:"var(--text-dim)" }}>clientes VIP y troncales</span></div>
        <p style={{ fontSize:12, color:"var(--text-dim)", margin:"0 0 12px" }}>
          Las alarmas fijas son globales («0 clientes registrados»). Acá se marca <strong>quién</strong> importa:
          un <strong>cliente</strong> avisa si cae cualquiera de sus líneas activas y dice cuáles; una <strong>línea</strong>
          {" "}tiene su propia alarma. Qué se chequea lo decide el tipo de línea: las que registran
          (<code>register</code>) se esperan registradas y con qualify; las troncales (<code>ip</code>) se miden
          solo por el qualify de PJSIP. No se vigila nada automáticamente: hoy la mayoría de las líneas no está
          registrada y encenderlo para todas sería ruido desde el primer minuto.
        </p>

        <div style={{ display:"flex", gap:8, alignItems:"flex-end", marginBottom:12, flexWrap:"wrap" }}>
          <div className="nv-form-field"><label className="nv-label">Vigilar</label>
            <select className="nv-select" value={wForm.tipo}
                    onChange={e=>setWForm(f=>({...f, tipo:e.target.value, entidad_id:""}))}>
              <option value="cliente">Un cliente (todas sus líneas)</option>
              <option value="linea">Una línea o troncal</option>
            </select></div>
          <div className="nv-form-field" style={{ flex:"1 1 260px" }}><label className="nv-label">Cuál</label>
            <select className="nv-select" value={wForm.entidad_id}
                    onChange={e=>setWForm(f=>({...f, entidad_id:e.target.value}))}>
              <option value="">— elegir —</option>
              {wForm.tipo === "cliente"
                ? (watchCand.clientes||[]).map(c=>(
                    <option key={c.entidad_id} value={c.entidad_id}>{c.nombre} ({c.lineas} línea{c.lineas===1?"":"s"})</option>))
                : (watchCand.lineas||[]).map(l=>(
                    <option key={l.entidad_id} value={l.entidad_id}>
                      {l.entidad_id} — {l.nombre} · {l.cliente} {l.auth_mode==="ip" ? "(troncal)" : ""}
                    </option>))}
            </select></div>
          <div className="nv-form-field"><label className="nv-label">Severidad</label>
            <select className="nv-select" value={wForm.severity} onChange={e=>setWForm(f=>({...f, severity:e.target.value}))}>
              {Object.entries(DICT.severidades).map(([v,l])=><option key={v} value={v}>{l}</option>)}
            </select></div>
          <div className="nv-form-field"><label className="nv-label">Canal</label>
            <select className="nv-select" value={wForm.channel} onChange={e=>setWForm(f=>({...f, channel:e.target.value}))}>
              {canales().map(c=><option key={c.v} value={c.v}>{c.l}</option>)}
            </select></div>
          <div className="nv-form-field" style={{ maxWidth:110 }}><label className="nv-label">Espera (s)</label>
            <input className="nv-input" type="number" min="0" value={wForm.debounce_seconds}
                   onChange={e=>setWForm(f=>({...f, debounce_seconds:e.target.value}))}
                   title="Cuánto debe sostenerse la caída antes de avisar. 300s evita el ruido de un reinicio de router."/></div>
          <button className="nv-btn nv-btn-primary" onClick={createWatch} disabled={wBusy || !wForm.entidad_id}>
            {wBusy ? <span className="nv-spinner"/> : "+ Vigilar"}</button>
        </div>

        {watch.length === 0 ? (
          <div style={{ fontSize:12, color:"var(--text-muted)" }}>Nada vigilado todavía.</div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            {watch.map(w=>(
              <div key={w.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 10px", background:"var(--bg-raised)", borderRadius:"var(--r-sm)" }}>
                <span style={{ flex:1, fontSize:13 }}>
                  {w.tipo === "cliente" ? "🏢" : (w.auth_mode === "ip" ? "🔌" : "📱")} <strong>{w.nombre}</strong>
                  <span style={{ color:"var(--text-dim)" }}>
                    {" · "}{w.tipo === "cliente" ? `${w.lineas} línea${w.lineas===1?"":"s"} activas` :
                             (w.auth_mode === "ip" ? "troncal — por qualify" : "línea — registro + qualify")}
                  </span>
                </span>
                <span style={{ fontSize:11, color:"var(--text-dim)" }}>espera {w.debounce_seconds}s</span>
                <span className={"nv-badge "+badgeForSeverity(w.severity)}>{tr("severidades", w.severity)}</span>
                <span className={"nv-badge "+badgeForChannel(w.channel)}>{tr("canales", w.channel)}</span>
                {!w.enabled && <span className="nv-badge">off</span>}
                {w.lineas === 0 && <span className="nv-badge nv-badge-warn" title="No hay líneas activas que vigilar">sin líneas</span>}
                <button className="nv-btn nv-btn-danger nv-btn-sm" onClick={()=>deleteWatch(w)} title="Dejar de vigilar">✕</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ────── HISTORIAL ────── */}
      <div className="nv-card">
        <div className="nv-card-header">
          <span className="nv-card-title">Historial</span>
          <div style={{ display: "flex", gap: 6 }}>
            <button className={"nv-btn nv-btn-sm" + (verAgrupado ? " nv-btn-primary" : "")}
                    onClick={() => setVerAgrupado(true)}>Agrupado (7 d)</button>
            <button className={"nv-btn nv-btn-sm" + (!verAgrupado ? " nv-btn-primary" : "")}
                    onClick={() => setVerAgrupado(false)}>Detalle (50)</button>
          </div>
        </div>
        <p style={{ fontSize:12, color:"var(--text-dim)", margin:"0 0 10px" }}>
          El texto de estos avisos y sus destinatarios se editan en <strong>Ajustes → 🔔 Mensajes de alarmas</strong>.
        </p>

        {verAgrupado ? (
          agrupado.length === 0 ? (
            <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Sin eventos en los últimos 7 días.</div>
          ) : (
            <table className="nv-table">
              <thead>
                <tr>
                  <th>Alerta</th>
                  <th>Severidad</th>
                  <th style={{ textAlign: "right" }}>Disparos</th>
                  <th style={{ textAlign: "right" }}>Envíos</th>
                  <th>Entrega</th>
                  <th>Último resumen</th>
                  <th>Última vez</th>
                </tr>
              </thead>
              <tbody>
                {agrupado.map(g => (
                  <tr key={g.alert_key}>
                    <td>{alertIcon(g.alert_key)} <strong>{g.etiqueta}</strong>
                      <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)" }}>{g.alert_key}</div>
                    </td>
                    <td><span className={"nv-badge " + badgeForSeverity(g.ultima_severity)}>{g.severidad}</span></td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>{g.disparos}</td>
                    <td style={{ textAlign: "right", color: "var(--text-dim)" }}>{g.envios}</td>
                    <td style={{ fontSize: 12 }}>
                      {g.entregados > 0 && <span className="nv-badge nv-badge-ok">{g.entregados} ok</span>}
                      {g.fallidos > 0 && <span className="nv-badge nv-badge-err" style={{ marginLeft: 4 }}>{g.fallidos} falló</span>}
                      {g.omitidos > 0 && <span className="nv-badge nv-badge-muted" style={{ marginLeft: 4 }}>{g.omitidos} omitida</span>}
                    </td>
                    <td style={{ fontSize: 12, maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        title={g.ultimo_summary}>{g.ultimo_summary}</td>
                    <td style={{ fontSize: 12, fontFamily: "var(--font-mono)" }}>{g.ultimo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : (
        <table className="nv-table">
          <thead>
            <tr>
              <th>Fecha (UTC)</th>
              <th>Alerta</th>
              <th>Evento</th>
              <th>Severidad</th>
              <th>Resumen</th>
              <th>Canal</th>
              <th>Resultado</th>
              <th>Detalle</th>
            </tr>
          </thead>
          <tbody>
            {history.map((h, i) => (
              <tr key={i}>
                <td style={{ fontSize: 12, fontFamily: "var(--font-mono)" }}>{h.ts}</td>
                <td>{alertIcon(h.alert_key)} {alertLabel(h.alert_key)}</td>
                <td><span className={"nv-badge " + badgeForEvent(h.event)}>{tr("estados", h.event)}</span></td>
                <td><span className={"nv-badge " + badgeForSeverity(h.severity)}>{tr("severidades", h.severity)}</span></td>
                <td style={{ fontSize: 12 }}>{h.summary}</td>
                <td>{tr("canales", h.channel)}</td>
                <td><span className={"nv-badge " + badgeForDelivery(h.delivery_status)}>{tr("entregas", h.delivery_status)}</span></td>
                <td style={{ fontSize: 12, color: "var(--text-dim)", maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {h.error_msg || ""}
                </td>
              </tr>
            ))}
            {history.length === 0 && (
              <tr><td colSpan={8} style={{ textAlign: "center", color: "var(--text-dim)" }}>Sin eventos todavía</td></tr>
            )}
          </tbody>
        </table>
        )}
      </div>

      {editing !== null && (
        <RuleModal
          rule={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); setMsg({ type: "success", text: "Regla guardada. El watcher la recarga en ≤10 min." }); loadAll(); }}
        />
      )}

      {kpiDel&&(
        <div className="nv-modal-overlay" onClick={()=>setKpiDel(null)}>
          <div className="nv-modal" style={{ maxWidth:380 }} onClick={e=>e.stopPropagation()}>
            <div className="nv-modal-header"><span className="nv-modal-title">Eliminar regla KPI</span>
              <button className="nv-modal-close" onClick={()=>setKpiDel(null)}>✕</button></div>
            <p style={{ fontSize:13, color:"var(--text-secondary)", lineHeight:1.6 }}>
              ¿Eliminar la regla <span style={{ color:"var(--danger)" }}>{kpiDel.name}</span>?</p>
            <div className="nv-modal-footer">
              <button className="nv-btn nv-btn-ghost" onClick={()=>setKpiDel(null)}>Cancelar</button>
              <button className="nv-btn nv-btn-danger" onClick={()=>deleteKpi(kpiDel)}>Eliminar</button></div>
          </div>
        </div>
      )}

      {sipDel&&(
        <div className="nv-modal-overlay" onClick={()=>setSipDel(null)}>
          <div className="nv-modal" style={{ maxWidth:380 }} onClick={e=>e.stopPropagation()}>
            <div className="nv-modal-header"><span className="nv-modal-title">Eliminar alerta SIP</span>
              <button className="nv-modal-close" onClick={()=>setSipDel(null)}>✕</button></div>
            <p style={{ fontSize:13, color:"var(--text-secondary)", lineHeight:1.6 }}>
              ¿Eliminar la alerta <span style={{ color:"var(--danger)" }}>{sipDel.name}</span>?</p>
            <div className="nv-modal-footer">
              <button className="nv-btn nv-btn-ghost" onClick={()=>setSipDel(null)}>Cancelar</button>
              <button className="nv-btn nv-btn-danger" onClick={()=>deleteSip(sipDel)}>Eliminar</button></div>
          </div>
        </div>
      )}

      {caDel&&(
        <div className="nv-modal-overlay" onClick={()=>setCaDel(null)}>
          <div className="nv-modal" style={{ maxWidth:380 }} onClick={e=>e.stopPropagation()}>
            <div className="nv-modal-header">
              <span className="nv-modal-title">Eliminar alerta</span>
              <button className="nv-modal-close" onClick={()=>setCaDel(null)}>✕</button>
            </div>
            <p style={{ fontSize:13, color:"var(--text-secondary)", lineHeight:1.6 }}>
              ¿Eliminar la alerta <span style={{ color:"var(--danger)" }}>{caDel.name}</span>?
            </p>
            <div className="nv-modal-footer">
              <button className="nv-btn nv-btn-ghost" onClick={()=>setCaDel(null)}>Cancelar</button>
              <button className="nv-btn nv-btn-danger" onClick={()=>deleteCustom(caDel)}>Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
